import CONSTANTS from 'src/utils/constants';

/** A finding Calc reports on a run that still succeeded, for example a group whose shares do not add up to 100%. */
export interface DASRunWarning {
    code: string;
    rowId?: string;
    message: string;
}

/** How an Execute run ended, as the WebSocket route answers it. */
export interface DasExecution {
    executionId: string;
    status: string;
    reason?: string;
    warnings?: DASRunWarning[];
}

/**
 * A message from the Execute route: a status update posted while the run goes on (IN PROGRESS, then SUCCEEDED, or
 * FAILED with the reason as its text), or the route's answer, which carries the run as JSON. A refusal is a FAILED
 * status with its reason.
 */
export type DasExecuteMessage =
    | { kind: 'status', status: string, message: string }
    | { kind: 'result', execution: DasExecution };

export const EXECUTE_NOT_DRAFT_REASON = 'Only the draft can be executed.';
export const EXECUTE_UNSAVED_REASON = 'Save your changes before executing the sheet.';
export const EXECUTE_SUCCEEDED_MESSAGE = 'The sheet executed. Its values and output are updated.';
export const EXECUTE_FAILED_MESSAGE = 'The sheet execution failed. Execute it again.';

/**
 * Reads a WebSocket message as an Execute message. Other routes' messages, and API Gateway's own notices such as
 * "Endpoint request timed out" (a run outlives the route's answer, so its result comes only as status updates), are
 * not Execute messages.
 * @param raw The message as received.
 * @returns The Execute message, or null when it is not one.
 */
export function parseDasExecuteMessage(raw: string): DasExecuteMessage | null {
    let parsed: { message?: string, messageType?: string, status?: string };
    try {
        parsed = JSON.parse(raw);
    } catch {
        return null;
    }
    if (parsed?.messageType !== CONSTANTS.WEB_SOCKET_API_ROUTES.EXECUTE_DERIVED_ALLOCATION_SHEET || !parsed.status) {
        return null;
    }
    const execution = asExecution(parsed.message);
    if (execution) {
        return { kind: 'result', execution };
    }
    return { kind: 'status', status: parsed.status, message: parsed.message ?? '' };
}

/** The run in a route answer, or null when the text is a status update or a refusal. */
function asExecution(message?: string): DasExecution | null {
    if (!message || !message.startsWith('{')) return null;
    try {
        const execution = JSON.parse(message) as DasExecution;
        return execution?.executionId && execution.status ? execution : null;
    } catch {
        return null;
    }
}

/**
 * Why the open version cannot be executed now. Only the draft in progress runs, and only once its steps are saved.
 * @param sheet The open version's status and version.
 * @param hasUnsavedWork Whether a step has changes that are not saved yet.
 * @returns The reason, or undefined when it can be executed.
 */
export function executeBlockedReason(sheet: { status: string, sheetVersion: number }, hasUnsavedWork: boolean): string | undefined {
    if (sheet.status !== 'DRAFT_IN_PROGRESS' || sheet.sheetVersion !== CONSTANTS.CALCULATION_VERSIONS.DRAFT_VERSION) {
        return EXECUTE_NOT_DRAFT_REASON;
    }
    return hasUnsavedWork ? EXECUTE_UNSAVED_REASON : undefined;
}

/**
 * The banner text for a run's warnings.
 * @param warnings The run's warnings.
 * @returns The text, or undefined when there are none.
 */
export function warningsMessage(warnings?: DASRunWarning[]): string | undefined {
    if (!warnings || warnings.length === 0) return undefined;
    return `The sheet executed with ${warnings.length === 1 ? 'a warning' : `${warnings.length} warnings`}: `
        + warnings.map((w) => w.message).join(' ');
}
